<template>
  <div class="flex flex-col space-y-4 lg:space-y-6">
    <div class="space-y-4">
      <Card>
        <div class="grid sm:grid-cols-2 sm:items-center gap-4">
          <div class="sm:text-lg">
            {{ $t("site.settings.language.labelUILanguage") }}
          </div>
          <div>
            <SelectSingleList
              :modelValue="locale"
              @update:modelValue="
                (newLocale) => newLocale && updateLocale(newLocale)
              "
              :options="locales"
            />
          </div>
        </div>
        <!-- <p class="mt-4 text-sm text-neutral-700 dark:text-neutral-300">
          Adjust the language of the text displayed on the site.
        </p> -->
      </Card>
      <Card>
        <div class="grid gap-4">
          <div class="flex space-x-4">
            <h2 class="sm:text-lg my-auto">
              {{ $t("site.settings.site.labelColorScheme") }}
            </h2>
          </div>
          <div class="flex space-x-4 justify-center">
            <NamiButton
              text
              :buttonType="theme === 'system' ? 'primary' : 'secondary'"
              @buttonClick="updateTheme('system')"
            >
              <div>
                <div
                  class="h-32 xl:w-44 w-32 hidden xl:block rounded-md bg-neutral-100 p-4 space-y-2"
                >
                  <div class="h-4 w-8 bg-neutral-300 rounded-md"></div>
                  <div class="h-4 w-16 bg-transparent rounded-md"></div>
                  <div class="h-4 w-20 bg-neutral-300 rounded-md"></div>
                  <div class="h-4 w-16 bg-neutral-300 rounded-md"></div>
                </div>
                <div class="flex my-2 space-x-2 justify-center">
                  <IconCircleCheck v-if="theme === 'system'" />
                  <IconCircle v-else />
                  <h3 class="select-none my-auto">
                    {{ $t("site.settings.site.radioSystem") }}
                  </h3>
                </div>
              </div>
            </NamiButton>
            <NamiButton
              text
              :buttonType="theme === 'light' ? 'primary' : 'secondary'"
              @buttonClick="updateTheme('light')"
            >
              <div>
                <div
                  class="h-32 xl:w-44 w-32 hidden xl:block rounded-md bg-neutral-100 p-4 space-y-2"
                >
                  <div class="h-4 w-8 bg-neutral-300 rounded-md"></div>
                  <div class="h-4 w-16 bg-transparent rounded-md"></div>
                  <div class="h-4 w-20 bg-neutral-300 rounded-md"></div>
                  <div class="h-4 w-16 bg-neutral-300 rounded-md"></div>
                </div>
                <div class="flex my-2 space-x-2 justify-center">
                  <IconCircleCheck v-if="theme === 'light'" />
                  <IconCircle v-else />
                  <h3 class="select-none my-auto">
                    {{ $t("site.settings.site.radioLight") }}
                  </h3>
                </div>
              </div>
            </NamiButton>
            <NamiButton
              text
              :buttonType="theme === 'dark' ? 'primary' : 'secondary'"
              @buttonClick="updateTheme('dark')"
            >
              <div>
                <div
                  class="h-32 xl:w-44 w-32 hidden xl:block rounded-md bg-black p-4 space-y-2"
                >
                  <div class="h-4 w-8 bg-neutral-800 rounded-md"></div>
                  <div class="h-4 w-16 bg-transparent rounded-md"></div>
                  <div class="h-4 w-24 bg-neutral-800 rounded-md"></div>
                  <div class="h-4 w-16 bg-neutral-800 rounded-md"></div>
                </div>
                <div class="flex my-2 space-x-2 justify-center">
                  <IconCircleCheck v-if="theme === 'dark'" />
                  <IconCircle v-else />
                  <h3 class="select-none my-auto">
                    {{ $t("site.settings.site.radioDark") }}
                  </h3>
                </div>
              </div>
            </NamiButton>
          </div>
        </div>
      </Card>
      <Card>
        <div class="grid sm:grid-cols-2 sm:items-center gap-4">
          <div class="flex space-x-4">
            <h2 class="sm:text-lg my-auto">
              {{ $t("site.settings.site.labelImageQuality") }}
            </h2>
          </div>
          <div class="flex space-x-4 justify-center sm:justify-end">
            <NamiButton
              v-for="qual in imageQualities"
              text
              :buttonType="quality === qual ? 'primary' : 'secondary'"
              @buttonClick="updateImageQuality(qual)"
            >
              <div class="flex my-2 space-x-2 justify-center">
                <IconCircleCheck v-if="quality === qual" />
                <IconCircle v-else />
                <h3 class="select-none my-auto">
                  {{ $t(`site.settings.site.radio${qual.capitalize()}`) }}
                </h3>
              </div>
            </NamiButton>
          </div>
        </div>
      </Card>
      <Card>
        <div class="grid sm:grid-cols-2 sm:items-center gap-4">
          <div class="flex space-x-4">
            <h2 class="sm:text-lg my-auto">Enable experimental features</h2>
          </div>
          <div class="flex space-x-4 sm:justify-end">
            <NamiSwitch v-model="isMiraiEnabled" />
          </div>
        </div>
      </Card>
      <div class="flex justify-end">
        <NamiButton
          class="sm:min-w-[15rem]"
          @buttonClick="emit('update')"
          buttonType="primary"
          :disabled="isUpdating"
        >
          {{ $t("site.settings.buttonSave") }}
        </NamiButton>
      </div>
    </div>
  </div>
  <NamiModalPresets
    v-model="showConfirmHighImageQualityModal"
    :options="{
      title: translate('site.settings.site.modalConfirmImageQualityTitle'),
      detail: translate('site.settings.site.modalConfirmImageQualityDetail'),
      icon: 'okay',
      buttons: [
        {
          buttonType: 'secondary',
          buttonText: translate(
            'site.settings.site.modalConfirmImageQualityButtonCancel',
          ),
          onButtonClick: () => (showConfirmHighImageQualityModal = false),
        },
        {
          buttonType: 'primary',
          buttonText: translate(
            'site.settings.site.modalConfirmImageQualityButtonConfirm',
          ),
          onButtonClick: () => updateImageQuality('high', true),
        },
      ],
    }"
  />
  <NamiModalPresets
    v-model="showConfirmLowImageQualityModal"
    :options="{
      title: translate('site.settings.site.modalConfirmImageQualityTitle'),
      detail:
        'Low quality images are recommended for users with metered connections.',
      icon: 'okay',
      buttons: [
        {
          buttonType: 'secondary',
          buttonText: translate(
            'site.settings.site.modalConfirmImageQualityButtonCancel',
          ),
          onButtonClick: () => (showConfirmLowImageQualityModal = false),
        },
        {
          buttonType: 'primary',
          buttonText: translate(
            'site.settings.site.modalConfirmImageQualityButtonConfirm',
          ),
          onButtonClick: () => updateImageQuality('low', true),
        },
      ],
    }"
  />
  <NamiNotificationsPresets ref="notifications" />
</template>

<script setup lang="ts">
import { IconCircle, IconCircleCheck } from "@tabler/icons-vue";
import {
  type AvailableLocales,
  type ColorTheme,
  type ImageQuality,
} from "~/src/api";
import NamiNotificationsPresets from "~/components/nami/notifications/NamiNotificationsPresets.vue";

if (process.server)
  throw new Error(
    "Settings components must be rendered on the client. Wrap the component with <client-only></client-only>",
  );

defineProps<{
  isUpdating?: boolean;
}>();
const emit = defineEmits<{
  (e: "update"): void;
}>();

const notifications = ref<InstanceType<typeof NamiNotificationsPresets>>();
const imageQualities: ["low", "medium", "high"] = ["low", "medium", "high"];

const nuxtApp = useNuxtApp();
const { t: translate } = useI18n();
const settingsStore = nuxtApp.$settings();
const { isMiraiEnabled } = useMirai();

const showConfirmHighImageQualityModal = ref(false);
const showConfirmLowImageQualityModal = ref(false);
const locales = getLocales();
const locale = computed(() => settingsStore?.settings.site.locale);

const theme = computed(() => settingsStore?.settings.site.theme);
function updateTheme(newTheme: ColorTheme) {
  if (!settingsStore) {
    return;
  }

  settingsStore.settings.site.theme = newTheme;
}

const quality = computed(() => {
  switch (settingsStore?.settings.site.quality) {
    case "data":
      return "high";
    case "dataSaver":
      return "medium";
    default:
      return settingsStore?.settings.site.quality;
  }
});

function updateImageQuality(quality: ImageQuality, force = false) {
  if (!settingsStore) {
    return;
  }

  if (quality === "high" && !force) {
    showConfirmHighImageQualityModal.value = true;
    return;
  }

  if (quality === "low" && !force) {
    showConfirmLowImageQualityModal.value = true;
    return;
  }

  settingsStore.settings.site.quality = quality;

  showConfirmHighImageQualityModal.value = false;
  showConfirmLowImageQualityModal.value = false;
}

function updateLocale(newLocale: AvailableLocales) {
  if (!settingsStore) {
    return;
  }

  settingsStore.settings.site.locale = newLocale;
}
</script>
